import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ApiVisa, EntityLoginEpService } from 'blg-akaun-ts-lib';
import { SessionActions } from '../../modules/session/session-controller/actions';
import { SessionSelectors } from '../../modules/session/session-controller/selectors';
import { SessionStates } from '../../modules/session/session-controller/states';
import { DropDownService } from '../../services/drop-down-service';
import { ReplaySubject, Subject, zip } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { forkJoin, from, iif, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, toArray } from 'rxjs/operators';
import { PermissionStates } from '../../modules/permission/permission-controller/root';
import { UserPermInquirySelectors } from '../../modules/permission/user-permissions-inquiry-controller/selectors';

@Component({
  selector: 'app-select-multi-cashbook-drop-down',
  templateUrl: './select-multi-cashbook-drop-down.component.html',
  styleUrls: ['./select-multi-cashbook-drop-down.component.css']
})
export class SelectMultiCashbookDropDownComponent implements OnInit, OnDestroy {

  @Input() cashbook: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = 'Branch';
  @Input() options: any;
  @Input() readPermissionDefintion;
  @Input() companyGuidList: any[];

  @Output() cashbookSelected = new EventEmitter<any>();

  userPermissionTarget$ = this.permissionStore.select(
    UserPermInquirySelectors.selectUserPermInquiry
  );

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key = 'cashbook';
  allSelected = false;
  filteredCache: any = [];
  cashbookTargets: string[]=[];

  /** flags to set the toggle all checkbox state */
  isIndeterminate = false;
  isChecked = false;

  protected _onDestroy = new Subject<void>();

  constructor(private dropDownService: DropDownService,
    private entityLoginEpService: EntityLoginEpService,
    private readonly store: Store<SessionStates>,
    private readonly permissionStore: Store<PermissionStates>
  ) {
  }

  ngOnInit() {
    // set initial selection
    this.store.select(SessionSelectors.selectDropDownRowData).subscribe(resolved => {
      this.rowData = resolved?resolved[this.key]?resolved[this.key]:[]:[];
      if (this.rowData.length === 0) {
          this.getRowData();
      }
      else {
        this.filteredOptions$.next(this.rowData.slice());
      }
    })

    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filtering();
        this.setToggleAllCheckboxState();
      });

    // listen for multi select field value changes
    this.cashbook.valueChanges
      .pipe(takeUntil(this._onDestroy)).subscribe(() => {
        this.setToggleAllCheckboxState();
      });

    this.userPermissionTarget$.subscribe((targets) => {
      let target = targets.filter(
        (target) => target.permDfn === this.readPermissionDefintion
      );
      let adminCreatePermissionTarget = targets.filter(
        (target) => target.permDfn === "TNT_TENANT_ADMIN"
      );
      let ownerCreatePermissionTarget = targets.filter(
        (target) => target.permDfn === "TNT_TENANT_OWNER"
      );

      if ( adminCreatePermissionTarget[0]?.hasPermission
        || ownerCreatePermissionTarget[0]?.hasPermission) {
        console.log("has Permission");
        this.cashbookTargets = [];
      } else {
        console.log("has targets");
        this.cashbookTargets = target[0]?.target !== null && Object.keys(target[0]?.target || {}).length !== 0
        ? target[0]?.target["bl_fi_mst_cashbook_hdr"]
        : [];
      }
    });
  }

  ngOnChanges() {
    if (this.isFilterCompany()) {
      this.getRowData();
    }
  }

  getRowData() {
    if (this.isFilterCompany() && !this.companyGuidList) return;
    let formData = {
      "limit" : null,
      "orderBy" : "code",
      "order" : "ASC"
    }

    if (this.companyGuidList) {
      Object.assign(formData,
        {
          "filterConditions": [
          {
            "filterColumn": "comp_guid",
            "filterValues": this.companyGuidList,
            "filterOperator": "IN"
          }]
        }
      )
    }
    this.dropDownService.get('cashbook/backoffice-ep', formData, this.apiVisa).subscribe(resolved => {
      this.rowData = resolved.data;
      if(this.cashbookTargets?.length>0){
        this.rowData = this.rowData.filter(record=>this.cashbookTargets.includes(record.guid))
      }
      this.store.dispatch(SessionActions.selectDropDownRowData({key: this.key, rowData: this.rowData}));
      //fill the drop down
      this.filteredOptions$.next(this.rowData.slice());

      // set default cashbook
      this.store.select(SessionSelectors.selectPersonalSettings).subscribe({next: (resolve: any) => {
        if (resolve?.DEFAULT_BRANCH) {
          if (this.isMultiple()) {
            this.cashbook.patchValue([resolve?.DEFAULT_BRANCH]);
          }
          else {
            this.cashbook.patchValue(resolve?.DEFAULT_BRANCH);
          }
        }
      }});
    });
  }

  filtering() {
    if (!this.rowData) {
      return;
    }
    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search) {
      this.filteredCache = this.rowData.slice();
      this.filteredOptions$.next(this.rowData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the cashbook
    this.filteredCache = this.rowData.filter((option) =>
    (option.code ?
      option.code.toLowerCase().indexOf(search) > -1 : null) ||
    (option.name ?
      option.name.toLowerCase().indexOf(search) > -1 : null));

    this.filteredOptions$.next(this.filteredCache);
  }

  onSelect(guid: string) {
    if (guid) {
      this.cashbook.patchValue(guid);
      this.selectedRow = this.rowData.find(row => row.guid === guid);
      this.cashbookSelected.emit(this.selectedRow);
    }
  }

  isRequired() {
    let value = this.options['required'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isMultiple() {
    let value = this.options['multiple'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.filteredOptions$.pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(val => {
        if (selectAllValue) {
          if (this.isSelectObject()) {
            this.cashbook.patchValue(val);
          }
          else {
            this.cashbook.patchValue([...val.map(item => item.guid)]);
          }
        } else {
          this.cashbook.patchValue([]);
        }
      }
    );
  }

  setToggleAllCheckboxState() {
    if (!this.isMultiple()) return;
    let filteredLength = 0;
    if (this.cashbook && this.cashbook.value) {
      this.filteredCache.forEach(el => {
        if (this.cashbook.value.indexOf(el) > -1) {
          filteredLength++;
        }
      });
      this.isIndeterminate = filteredLength > 0 && filteredLength < this.filteredCache.length;
      this.isChecked = filteredLength > 0 && filteredLength === this.filteredCache.length;
    }
  }

  isSelectObject() {
    let value = this.options['selectObject'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  showValue(option) {
    if (this.isSelectObject()) {
      return option;
    }
    else {
      return option.guid;
    }
  }

  isFilterCompany() {
    let value = this.options['filterCompany'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
